/**
 * The live chat console — Super-Admin state.
 *
 * The opposite commit model to `useChatWidgetsStore`. A widget's configuration is
 * a coherent whole and is held as a draft until saved; a conversation is not.
 * Claiming, replying, handing back and ending are each a single act with an
 * immediate consequence for someone who is sitting there waiting, so every action
 * commits on the spot and the server returns the whole conversation back. The
 * store then replaces its state wholesale rather than splicing an optimistic
 * update in — on a live chat, a message shown as sent that was not is worse than
 * a moment's latency.
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  ChatQueueCountsDto,
  ChatSessionDetailDto,
  ChatSessionStatusFilter,
  ChatSessionSummaryDto,
} from '@respectly/shared-types';

const E = API_ENDPOINTS.SUPER_ADMIN.CHAT;

interface SessionListResponse {
  sessions: ChatSessionSummaryDto[];
  counts: ChatQueueCountsDto;
}

interface LiveChatsState {
  sessions: ChatSessionSummaryDto[];
  counts: ChatQueueCountsDto | null;

  status: ChatSessionStatusFilter;
  brandId: string | null;

  activeId: string | null;
  active: ChatSessionDetailDto | null;

  loadingList: boolean;
  loadingThread: boolean;
  /** One flag for every write, because the action bar disables as a unit. */
  acting: boolean;
  error: string | null;

  loadSessions: () => Promise<void>;
  setStatus: (status: ChatSessionStatusFilter) => Promise<void>;
  setBrand: (brandId: string | null) => Promise<void>;
  open: (id: string | null) => Promise<void>;
  /** Re-read the open conversation in place, without the spinner. */
  refreshActive: () => Promise<void>;
  claim: () => Promise<boolean>;
  reply: (text: string) => Promise<boolean>;
  release: () => Promise<boolean>;
  close: () => Promise<boolean>;
}

function message(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export const useLiveChatsStore = create<LiveChatsState>((set, get) => ({
  sessions: [],
  counts: null,
  status: 'open',
  brandId: null,
  activeId: null,
  active: null,
  loadingList: false,
  loadingThread: false,
  acting: false,
  error: null,

  loadSessions: async () => {
    set({ loadingList: true });
    try {
      const { status, brandId } = get();
      const api = getApiClient();
      const result = await api.get<SessionListResponse>(E.SESSIONS, {
        status,
        ...(brandId ? { brandId } : {}),
      });
      set({
        sessions: result?.sessions ?? [],
        counts: result?.counts ?? null,
        loadingList: false,
      });
    } catch (err) {
      set({ loadingList: false, error: message(err, 'Failed to load conversations') });
    }
  },

  setStatus: async (status) => {
    set({ status });
    await get().loadSessions();
  },

  setBrand: async (brandId) => {
    set({ brandId });
    await get().loadSessions();
  },

  open: async (id) => {
    if (!id) {
      set({ activeId: null, active: null });
      return;
    }
    set({ activeId: id, loadingThread: true, error: null });
    try {
      const api = getApiClient();
      const detail = await api.get<ChatSessionDetailDto>(E.SESSION(id));
      // Guard against a slow response for a conversation the user has moved on
      // from: without this, clicking through the queue can land the wrong thread.
      if (get().activeId !== id) return;
      set({ active: detail ?? null, loadingThread: false });
    } catch (err) {
      if (get().activeId !== id) return;
      set({ loadingThread: false, error: message(err, 'Failed to open the conversation') });
    }
  },

  /**
   * The open conversation, re-read quietly on a timer.
   *
   * `open()` cannot do this job: it raises `loadingThread`, which would blink the
   * thread into a spinner every fifteen seconds. Until this existed the transcript
   * only ever moved when *you* acted on it — so after taking a chat over, an
   * admin watched a thread that could not show them the visitor's reply, which is
   * the same complaint as the widget's, seen from the other side.
   *
   * Silent on failure and skipped mid-write: a transient blip must not replace a
   * readable conversation with an error, and a refresh landing on top of a claim
   * would overwrite the server's answer with a staler one.
   */
  refreshActive: async () => {
    const { activeId, acting } = get();
    if (!activeId || acting) return;
    try {
      const detail = await getApiClient().get<ChatSessionDetailDto>(E.SESSION(activeId));
      if (!detail || get().activeId !== activeId || get().acting) return;
      set({ active: detail });
    } catch {
      // The next tick tries again.
    }
  },

  claim: () => act(set, get, (id) => getApiClient().post<ChatSessionDetailDto>(E.SESSION_CLAIM(id))),

  reply: (text) =>
    act(set, get, (id) =>
      getApiClient().post<ChatSessionDetailDto>(E.SESSION_REPLY(id), { text })
    ),

  release: () =>
    act(set, get, (id) => getApiClient().post<ChatSessionDetailDto>(E.SESSION_RELEASE(id))),

  close: () =>
    act(set, get, (id) => getApiClient().post<ChatSessionDetailDto>(E.SESSION_CLOSE(id))),
}));

/**
 * Every action is the same shape: act on the open conversation, take the server's
 * version of it back, and refresh the queue so the row's mode and preview follow.
 */
async function act(
  set: (partial: Partial<LiveChatsState>) => void,
  get: () => LiveChatsState,
  run: (id: string) => Promise<ChatSessionDetailDto>
): Promise<boolean> {
  const id = get().activeId;
  if (!id) return false;
  set({ acting: true, error: null });
  try {
    const detail = await run(id);
    set({ acting: false, ...(detail ? { active: detail } : {}) });
    await get().loadSessions();
    return true;
  } catch (err) {
    set({ acting: false, error: message(err, 'That did not go through') });
    return false;
  }
}
